(() => {
  const DESKTOP_MQ = '(min-width: 991px)';
  const OPEN_CLASS = 'pbpm-smyle-gift-open';
  const MOBILE_DETAIL_MAX = 700;

  /** @type {WeakMap<HTMLElement, GiftPopupController>} */
  const controllers = new WeakMap();

  /**
   * @param {string} selector
   * @param {ParentNode} [root]
   * @returns {HTMLElement | null}
   */
  const qs = (selector, root = document) => root.querySelector(selector);

  /**
   * @param {string} selector
   * @param {ParentNode} [root]
   * @returns {HTMLElement[]}
   */
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];

  const isDesktop = () => window.matchMedia(DESKTOP_MQ).matches;

  /**
   * @param {HTMLElement} popup
   */
  function GiftPopupController(popup) {
    this.popup = popup;
    this.dialog = qs('.pb-pm-smyle-gift__dialog', popup);
    this.detail = qs('[data-pm-smyle-gift-detail]', popup);
    this.hero = qs('[data-pm-smyle-gift-hero]', popup);
    this.backBtn = qs('[data-pm-smyle-gift-back]', popup);
    this.swiperEl = qs('[data-pm-smyle-gift-swiper]', popup);
    this.dotsEl = qs('[data-pm-smyle-gift-dots]', popup);
    this.cards = qsa('[data-pm-smyle-gift-card]', popup);
    this.slides = qsa('[data-pm-smyle-gift-slide]', popup);
    /** @type {object | null} */
    this.swiper = null;
    /** @type {number} */
    this.activeIndex = 0;
    /** @type {boolean} */
    this.showingDetail = false;
    /** @type {HTMLElement | null} */
    this.lastFocus = null;
    /** @type {number | null} */
    this.swiperWaitTimer = null;
    /** @type {number | null} */
    this.mobileDialogHeight = null;
  }

  /**
   * @param {boolean} open
   */
  GiftPopupController.prototype.setOpen = function setOpen(open) {
    const popup = this.popup;

    popup.hidden = !open;
    popup.classList.toggle('is-open', open);
    popup.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.documentElement.classList.toggle(OPEN_CLASS, open);
    document.body.classList.toggle(OPEN_CLASS, open);

    if (open) {
      window.PBOverlayStack?.push(popup);
      const closeBtn = qs('[data-pm-smyle-gift-close]', popup);
      window.setTimeout(() => closeBtn?.focus({ preventScroll: true }), 0);
      return;
    }

    window.PBOverlayStack?.pop(popup);
    this.showOverview();
    this.clearMobileDialogHeight();

    const focusTarget = this.lastFocus;
    this.lastFocus = null;

    if (focusTarget instanceof HTMLElement) {
      window.setTimeout(() => {
        focusTarget.focus({ preventScroll: true });
        if (typeof focusTarget.blur === 'function') {
          focusTarget.blur();
        }
      }, 0);
    }
  };

  GiftPopupController.prototype.destroySwiper = function destroySwiper() {
    if (this.swiperWaitTimer) {
      window.clearInterval(this.swiperWaitTimer);
      this.swiperWaitTimer = null;
    }

    if (!this.swiper) return;

    this.swiper.destroy(true, true);
    this.swiper = null;
  };

  /**
   * @param {() => void} onReady
   */
  GiftPopupController.prototype.waitForSwiper = function waitForSwiper(onReady) {
    if (typeof Swiper !== 'undefined') {
      onReady();
      return;
    }

    if (this.swiperWaitTimer) return;

    let tries = 0;
    this.swiperWaitTimer = window.setInterval(() => {
      tries += 1;

      if (typeof Swiper !== 'undefined' || tries > 80) {
        window.clearInterval(this.swiperWaitTimer);
        this.swiperWaitTimer = null;

        if (typeof Swiper !== 'undefined') {
          onReady();
        }
      }
    }, 50);
  };

  GiftPopupController.prototype.initSwiper = function initSwiper() {
    if (isDesktop() || !this.showingDetail || !this.swiperEl) return;

    if (typeof Swiper === 'undefined') {
      this.waitForSwiper(() => this.initSwiper());
      return;
    }

    if (this.swiper) {
      this.swiper.update();
      this.swiper.slideTo(this.activeIndex, 0);
      return;
    }

    this.swiper = new Swiper(this.swiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      autoHeight: false,
      // Each slide scrolls vertically, so Swiper must leave touchstart alone
      // and only claim clearly horizontal gestures.
      touchStartPreventDefault: false,
      touchReleaseOnEdges: true,
      touchAngle: 35,
      threshold: 6,
      initialSlide: this.activeIndex,
      pagination: this.dotsEl
        ? { el: this.dotsEl, clickable: true }
        : false,
      on: {
        slideChange: (swiper) => {
          this.selectGift(swiper.activeIndex, { fromSwiper: true });
        },
      },
    });
  };

  GiftPopupController.prototype.ensureSwiper = function ensureSwiper() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.initSwiper());
    });
  };

  GiftPopupController.prototype.captureMobileDialogHeight = function captureMobileDialogHeight() {
    if (isDesktop() || !this.dialog || !this.showingDetail) return;

    const measured = Math.round(this.dialog.getBoundingClientRect().height);
    const viewportCap = Math.round(window.innerHeight - 42);
    this.mobileDialogHeight = Math.min(measured, MOBILE_DETAIL_MAX, viewportCap);
    this.dialog.style.setProperty(
      '--pb-pm-smyle-gift-mobile-height',
      `${this.mobileDialogHeight}px`,
    );
  };

  GiftPopupController.prototype.clearMobileDialogHeight = function clearMobileDialogHeight() {
    this.mobileDialogHeight = null;

    if (this.dialog) {
      this.dialog.style.removeProperty('--pb-pm-smyle-gift-mobile-height');
    }
  };

  /**
   * @param {number} index
   * @param {{ fromSwiper?: boolean }} [options]
   */
  GiftPopupController.prototype.selectGift = function selectGift(index, options = {}) {
    const wasOverview = !this.showingDetail;
    const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));

    if (!isDesktop() && wasOverview) {
      this.captureMobileDialogHeight();
    }

    this.activeIndex = nextIndex;
    this.showingDetail = true;
    this.popup.classList.add('is-detail');

    if (this.detail) this.detail.hidden = false;
    if (this.backBtn) this.backBtn.hidden = isDesktop();

    for (const [cardIndex, card] of this.cards.entries()) {
      const selected = cardIndex === nextIndex;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    for (const [slideIndex, slide] of this.slides.entries()) {
      slide.classList.toggle('is-active', slideIndex === nextIndex);
    }

    // Every gift opens at the top of its own story, whichever element carries
    // the scroll at this breakpoint (the slide on mobile, the copy on desktop).
    const activeSlide = this.slides[nextIndex];

    if (activeSlide) {
      activeSlide.scrollTop = 0;
      const copy = qs('.pb-pm-smyle-gift__slide-copy', activeSlide);
      if (copy) copy.scrollTop = 0;
    }

    if (isDesktop()) {
      this.destroySwiper();
      return;
    }

    this.ensureSwiper();

    if (this.swiper && !options.fromSwiper) {
      this.swiper.slideTo(nextIndex, 0);
    }
  };

  GiftPopupController.prototype.showOverview = function showOverview() {
    this.showingDetail = false;
    this.activeIndex = 0;
    this.popup.classList.remove('is-detail');
    this.destroySwiper();
    this.clearMobileDialogHeight();

    if (this.detail) this.detail.hidden = true;
    if (this.backBtn) this.backBtn.hidden = true;

    for (const card of this.cards) {
      card.classList.remove('is-selected');
      card.setAttribute('aria-pressed', 'false');
    }

    for (const slide of this.slides) {
      slide.classList.remove('is-active');
    }
  };

  /**
   * @param {{ index?: number, restoreFocusTo?: HTMLElement | null }} [options]
   */
  GiftPopupController.prototype.open = function open(options = {}) {
    this.lastFocus =
      options.restoreFocusTo instanceof HTMLElement
        ? options.restoreFocusTo
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

    this.setOpen(true);

    if (Number.isInteger(options.index)) {
      this.selectGift(options.index);
      return;
    }

    this.showOverview();
  };

  GiftPopupController.prototype.close = function close() {
    this.setOpen(false);
  };

  GiftPopupController.prototype.handleResize = function handleResize() {
    if (this.popup.hidden || !this.showingDetail) return;

    this.selectGift(this.activeIndex);
  };

  /**
   * @param {HTMLElement} popup
   * @returns {GiftPopupController}
   */
  function getController(popup) {
    let controller = controllers.get(popup);

    if (!controller) {
      controller = new GiftPopupController(popup);
      controllers.set(popup, controller);
    }

    return controller;
  }

  /**
   * @param {HTMLElement | null} [from]
   * @returns {HTMLElement | null}
   */
  function findPopup(from) {
    if (from instanceof HTMLElement) {
      const scoped = from.closest('[data-pb-pick-mix]')?.querySelector('[data-pm-smyle-gift-popup]');
      if (scoped) return scoped;
    }

    // Prefer a popup whose section is visible: with the Convert A/B setup two
    // pick & mix sections coexist and the first one may be display:none.
    const popups = qsa('[data-pm-smyle-gift-popup]');
    const visible = popups.find((popup) => {
      const root = popup.closest('[data-pb-pick-mix]');
      return !root || root.offsetParent !== null;
    });

    return visible || popups[0] || null;
  }

  function bind() {
    if (document.documentElement.dataset.pmSmyleGiftBound === 'true') return;
    document.documentElement.dataset.pmSmyleGiftBound = 'true';

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const opener = target.closest('[data-pm-smyle-gift-open]');
      if (opener instanceof HTMLElement) {
        event.preventDefault();
        const popup = findPopup(opener);
        if (!popup) return;

        const indexAttr = opener.getAttribute('data-pm-smyle-gift-index');
        const index = indexAttr === null ? undefined : Number(indexAttr);
        getController(popup).open({
          index: Number.isInteger(index) ? index : undefined,
          restoreFocusTo: opener,
        });
        return;
      }

      const closer = target.closest('[data-pm-smyle-gift-close]');
      if (closer) {
        const popup = closer.closest('[data-pm-smyle-gift-popup]');
        if (popup) getController(popup).close();
        return;
      }

      const back = target.closest('[data-pm-smyle-gift-back]');
      if (back) {
        const popup = back.closest('[data-pm-smyle-gift-popup]');
        if (popup) getController(popup).showOverview();
        return;
      }

      const card = target.closest('[data-pm-smyle-gift-card]');
      if (card instanceof HTMLElement) {
        const popup = card.closest('[data-pm-smyle-gift-popup]');
        if (!popup) return;

        getController(popup).selectGift(Number(card.dataset.pmSmyleGiftIndex || 0));
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      const popup = qs('[data-pm-smyle-gift-popup].is-open');
      if (popup) getController(popup).close();
    });

    window.addEventListener('resize', () => {
      for (const popup of qsa('[data-pm-smyle-gift-popup]')) {
        getController(popup).handleResize();
      }
    });
  }

  /**
   * @param {{ index?: number }} [options]
   */
  function open(options = {}) {
    const popup = findPopup();
    if (!popup) return;

    getController(popup).open(options);
  }

  function close() {
    const popup = qs('[data-pm-smyle-gift-popup].is-open') || findPopup();
    if (!popup) return;

    getController(popup).close();
  }

  window.PBSmyleGiftPopup = { open, close };

  /**
   * Campaign landing links use ?pop-up=yes to open the gift overview on load.
   */
  function autoOpenFromUrl() {
    if (new URLSearchParams(window.location.search).get('pop-up') !== 'yes') return;
    if (window.Shopify?.designMode) return;

    const run = () => {
      if (!findPopup()) return;
      open();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
      return;
    }

    run();
  }

  bind();
  autoOpenFromUrl();
})();
